/**
 * Breed Dropdown Component
 * Multi-select dropdown for breed filter on main catalog
 */

import { useState, useEffect, useRef } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/services';

interface Breed {
  id: string;
  name: string;
  species_key: string;
}

interface BreedDropdownProps {
  speciesKey: string | null;
  selectedBreedIds: string[];
  onToggle: (breed: { id: string; name: string }) => void;
}

const BreedDropdown = ({
  speciesKey,
  selectedBreedIds,
  onToggle,
}: BreedDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch breeds when dropdown opens or query/species changes
  useEffect(() => {
    if (!isOpen) return;

    const fetchBreeds = async () => {
      setIsLoading(true);
      try {
        if (speciesKey) {
          // Single species
          const { data, error } = await supabase.rpc('get_species_breeds', {
            p_species_key: speciesKey,
            p_query: query || null,
            p_limit: 30,
          });
          if (error) throw error;
          setBreeds(data || []);
        } else {
          // Both species - fetch dog and cat breeds
          const [dogRes, catRes] = await Promise.all([
            supabase.rpc('get_species_breeds', {
              p_species_key: 'dog',
              p_query: query || null,
              p_limit: 20,
            }),
            supabase.rpc('get_species_breeds', {
              p_species_key: 'cat',
              p_query: query || null,
              p_limit: 20,
            }),
          ]);
          const combined = [...(dogRes.data || []), ...(catRes.data || [])];
          setBreeds(combined);
        }
      } catch (error) {
        logger.error('Failed to fetch breeds', {
          context: { feature: 'catalog', function_name: 'fetchBreeds' },
          error: error as Error,
          error_code: 'breeds_fetch_failed',
          error_type: 'unexpected',
        });
        setBreeds([]);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(fetchBreeds, 200);
    return () => clearTimeout(timer);
  }, [isOpen, speciesKey, query]);

  // Reset query when species changes
  useEffect(() => {
    setQuery('');
  }, [speciesKey]);

  const handleToggle = (breed: Breed) => {
    onToggle({ id: breed.id, name: breed.name });
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger chip */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center justify-center gap-2 h-[40px] px-4 transition-colors active:opacity-80 ${
          selectedBreedIds.length > 0 ? 'bg-bg-accent-pink ring-2 ring-text-primary/30' : 'bg-bg-accent-pink-secondary'
        }`}
        style={{
          borderRadius: '20px',
          fontFamily: '"CoFo Gothic", system-ui, sans-serif',
        }}
      >
        <span className="text-text-primary text-[14px] font-medium whitespace-nowrap">
          Порода
        </span>
        <ChevronDown 
          className={`w-4 h-4 text-text-primary transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div 
          className="absolute top-full left-0 mt-2 w-[280px] bg-white rounded-2xl shadow-lg border border-border z-50 overflow-hidden"
          style={{ fontFamily: '"CoFo Gothic", system-ui, sans-serif' }}
        >
          {/* Search input */}
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск породы..."
                className="w-full h-10 pl-9 pr-3 rounded-xl bg-bg-accent-pink-secondary text-sm text-text-primary placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-text-primary/20"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>

          {/* Breeds list */}
          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                Загрузка...
              </div>
            ) : breeds.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                {query ? 'Не найдено' : 'Нет доступных пород'}
              </div>
            ) : (
              breeds.map((breed) => {
                const isSelected = selectedBreedIds.includes(breed.id);
                return (
                  <button
                    key={breed.id}
                    onClick={() => handleToggle(breed)}
                    className="w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-bg-accent-pink-secondary transition-colors"
                  >
                    <span
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected
                          ? 'bg-text-primary border-text-primary'
                          : 'border-border bg-white'
                      }`}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </span>
                    <span className="text-text-primary text-sm flex-1">
                      {breed.name}
                    </span>
                    {/* Show species emoji when no species filter is active */}
                    {!speciesKey && (
                      <span className="text-xs text-muted-foreground">
                        {breed.species_key === 'dog' ? '🐕' : '🐈'}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Selected count footer */}
          {selectedBreedIds.length > 0 && (
            <div className="px-4 py-3 border-t border-border bg-bg-accent-pink-secondary">
              <span className="text-sm text-text-primary">
                Выбрано: {selectedBreedIds.length}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BreedDropdown;
